import React, { useState, useRef } from 'react';
import { ReactForm, ReactFormField } from '@adrenalin/react-form';
import ReCAPTCHA from 'react-google-recaptcha';

import { googleRecaptchaKey } from '../constants';
import Configs from '../account/constants/Configs';
import Modal from './Modal';
import useAsyncState from '../hooks/useAsyncState';
import BasicVarAPI from '../helpers/BasicVarAPI';
import Helpers from '../helpers/util';

import smoothscroll from '../modules/smoothscroll';

const debugMode = Helpers.getParameterByName('debugForm');

/**
 * Object to store form values until user passes recaptcha test
 */
window.competition_form_data = {};

const CompetitionForm = ({ data }) => {
	/**
	 * State variables, refs, etc.
	 */
	const recaptchaRef = useRef(null);
	const [state, actions] = useAsyncState();
	const [showTerms, setShowTerms] = useState(false);

	/**
	 * Custom validation functions to pass to ReactForm
	 */
	const customValidators = [
		{
			ID: 'match',
			validate: (input, pattern) => {
				const reg = new RegExp(pattern);
				const result = reg.test(input.value);
				return result;
			},
			error: 'This input is invalid',
		},
	];

	/**
	 * Posts the form data to VAR
	 * to be called after the user has passed the recaptcha validation
	 */
	const postFormData = (captchaResponse) => {
		actions.request();
		BasicVarAPI.POST(
			`https://${Configs.competitionAPI}?captchaResponse=${captchaResponse}`,
			window.competition_form_data
		)
			.then(actions.success)
			.then(() => {
				smoothscroll.animateScroll(document.querySelector('#enter'));
			})
			.catch((error) => {
				console.error(error);
				actions.fail(
					error.friendly_message || error.message || 'Something went wrong'
				);
				smoothscroll.animateScroll(
					document.getElementById('competition-error')
				);
			});
	};

	/**
	 * Get's called whenever the user passes a recaptcha test.
	 * If the recaptcha key is null, google has expired the token that the user previously had.
	 */
	const handleRecaptchaChange = (key = null) => {
		recaptchaRef.current.reset();
		if (key) {
			postFormData(key);
		}
	};

	/**
	 * Called by ReactForms onSuccess handler - formValues is an object of form input values
	 * Rather than directly submitting the form, we tell recaptcha to test the user
	 */
	const handleSubmit = (formValues) => {
		window.competition_form_data = {
			competition_id: data.key,
			...formValues,
		};

		recaptchaRef.current.execute();
	};

	/**
	 * If the is_closed flag is true from kentico,
	 * inform the user that the competition is closed
	 */
	if (data.is_closed) {
		return (
			<div className="rich-text">
				<h2>This competition is now closed</h2>
				<p>{data.closed_message}</p>
				<p className="cta">
					<a href="/">
						Go to homepage <i className="icon icon-arrow-right-2"></i>
					</a>
				</p>
			</div>
		);
	}

	/**
	 * If the user has successfulyy submitted the form, show the success state
	 */
	if (state.success) {
		return (
			<div className="rich-text">
				<p className="success-icon">
					<i className="icon icon-tick-circle"></i>
				</p>
				<h2>Thank you for your entry</h2>
				<p>{data.success_message}</p>
				<p className="cta">
					<a href="/">
						Go to homepage <i className="icon icon-arrow-right-2"></i>
					</a>
				</p>
			</div>
		);
	}

	/**
	 * Otherwise, render the form
	 */
	return (
		<div>
			<Modal active={showTerms} onClose={() => setShowTerms(false)}>
				<div
					dangerouslySetInnerHTML={{ __html: data.terms_and_conditions }}
				></div>
			</Modal>

			<div className="rich-text">
				<h2>Enter now</h2>
				<p>{data.short_description}</p>
			</div>

			<ReactForm
				validators={customValidators}
				onSuccess={handleSubmit}
				className={state.loading ? 'loading' : ''}
			>
				<ReactFormField
					labelplaceholder="First Name"
					type="text"
					name="first_name"
					validators={['name']}
					required
				/>
				<ReactFormField
					labelplaceholder="Last Name"
					type="text"
					name="last_name"
					validators={['name']}
					required
				/>
				<ReactFormField
					labelplaceholder="Email"
					type="text"
					name="email"
					validators={['email']}
					required
				/>
				{data.mobile_number.visible ? (
					<ReactFormField
						type="text"
						labelplaceholder={data.mobile_number.label}
						name="mobile_number"
						required={data.mobile_number.required}
						validators={['mobile']}
					/>
				) : null}
				{data.promo_code.visible ? (
					<ReactFormField
						labelplaceholder={data.promo_code.label}
						type="text"
						name="promo_code"
						required={data.promo_code.required}
						validators={[`match:${data.promo_code.validation}`]}
						errorMessages={{ match: 'Not a valid promo code' }}
						info={data.promo_code.info || ""}
					/>
				) : null}
				{data.question.visible ? (
					<ReactFormField
						label={data.question.label}
						placeholder="Enter your answer"
						type="textarea"
						name="answer"
						required={data.question.required}
					/>
				) : null}
				{data.terms_and_conditions_checkbox.visible ? (
					<ReactFormField
						label={
							<span>
								I agree to the{' '}
								<a
									onClick={(event) => {
										event.preventDefault();
										setShowTerms(true);
									}}
								>
									terms & conditions
								</a>
							</span>
						}
						type="checkbox"
						name="terms_and_conditions_checkbox"
						required={data.terms_and_conditions_checkbox.required}
					/>
				) : null}
				{data.age_checkbox.visible ? (
					<ReactFormField
						label={data.age_checkbox.label}
						type="checkbox"
						name="age_checkbox"
						required={data.age_checkbox.required}
					/>
				) : null}
				{data.general_checkbox.visible ? (
					<ReactFormField
						label={data.general_checkbox.label}
						type="checkbox"
						name="general_checkbox"
						required={data.general_checkbox.required}
					/>
				) : null}
				<ReCAPTCHA
					size="invisible"
					ref={recaptchaRef}
					sitekey={googleRecaptchaKey}
					onChange={handleRecaptchaChange}
				></ReCAPTCHA>
				<button disabled={state.loading} type="submit" className="button">
					{state.loading ? 'Loading...' : 'Submit'}
				</button>
			</ReactForm>

			<p id="competition-error" className="error">
				{state.error || ''}
			</p>
		</div>
	);
};

export default CompetitionForm;
